/**
 * @file PowerBICtl.js
 * @module PowerBICtl
 * @description PowerBI画面制御モジュール。Power BIレポートの埋め込み・イベント制御を担当します。
 */
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { toast } from 'react-toastify';
import { PowerBIAuth } from './PowerBIAuth';
import { logToCloudWatch } from './Rum-init';
import { SC_STATE , C_URLS , C_LOGWORDING , C_ERRWORDING } from './Constants';
import './PowerBICtl.css';

/**
 * Power BI 埋め込み画面制御コンポーネント
 * @component
 * @param {Object} props
 * @param {Object} props.embedData - 埋め込み情報（トークン・URL等）
 * @param {string} props.token - 初回取得したトークン
 * @param {function} props.setLoadingState - ローディング状態を更新するコールバック
 * @param {function} props.setEmbedData - トークンと埋め込み情報を更新するコールバック
 * @param {function} props.setError - エラー通知用コールバック（App.jsから受け取る）
 * @returns {JSX.Element} Power BI 埋め込みレポート
 */
const PowerBICtl = ({ embedData, cnsltokenRef, setLoadingState, setEmbedData, setError }) => {
  const { bitoken: biTokenVal, embedURL: embedUrl } = embedData;

  // embedUrl から reportId を抽出
  const urlObj = new URL(embedUrl);
  const reportId = urlObj.searchParams.get("reportId");

  const embedConfig = {
    type: 'report',
    id: reportId,
    embedUrl: embedUrl,
    accessToken: biTokenVal,
    tokenType: models.TokenType.Embed,
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: false
        }
      }
    }
  };

  const eventHandlers = new Map([
    ['loaded', () => {
      //console.log('✅ Power BI レポートが読み込まれました');
    }],
    ['rendered', () => {
      //console.log('✅ Power BI レポートが描画されました');
    }],
    ['error', (event) => {
      const errorDetail = event?.detail || {};
      const errorCode = errorDetail.errorCode || 'UNKNOWN_ERROR';
      const errorMessage = errorDetail.message || 'Power BI の読み込み中にエラーが発生しました';
      const detailedMessage = errorDetail.detailedMessage || '詳細情報はありません';

      //console.error('❌ Power BI 埋め込みエラー errorCode:', errorCode, event.detail);

      // エラー情報をApp.jsに通知
      if (typeof setError === 'function') {
        setError({
          errorCode,
          errorMessage,
          detailedMessage,
        });
      }

      switch (errorMessage) {        // エラーメッセージに応じて処理を分岐
        // トークンの期限切れや無効なトークンの場合
        case 'TokenExpired':
          const updateToken = async () => {
            try {
              setLoadingState?.(SC_STATE.BI_TOKEN_REFETCHING);
              //console.log('[PowerBICtl-eventHandlers][TokenExpired]token=\n', cnsltokenRef.current);
              const result = await PowerBIAuth(cnsltokenRef.current, setEmbedData, setLoadingState);
              const { bitoken: newBiToken } = result;

              if (newBiToken) {
                window.report.setAccessToken(newBiToken);
                toast.success('情報が更新され、再表示されました。');
                setLoadingState?.(SC_STATE.AUTHENTICATED);
              } else {
                toast.error('情報更新に失敗しました。再ログインしてください。');
                logToCloudWatch(`不明エラー\nErrCode:${errorCode}\nErrlog:${errorMessage}\n詳細: ${detailedMessage}`,'FRONTEND_ERROR','CRITICAL');
                setLoadingState?.(SC_STATE.BI_ERR_NONPERMANENT);
              }
            } catch (error) {
              toast.error('情報更新中にエラーが発生しました。再ログインしてください。');
              logToCloudWatch(`不明エラー\nErrCode:${errorCode}\nErrlog:${errorMessage}\n詳細: ${detailedMessage}`,'FRONTEND_ERROR','CRITICAL');
              setLoadingState?.(SC_STATE.BI_ERR_NONPERMANENT);
            }
          };
          updateToken();
          break;
        // 一時的なエラー
        case 'Invalid parameters':
        case 'OpenConnectionError':
        case 'ExplorationContainer_FailedToLoadModel_DefaultDetails':
          logToCloudWatch(`一時的エラー\nErrCode:${errorCode}\nErrlog:${errorMessage}\n詳細: ${detailedMessage}`,'FRONTEND_ERROR','CRITICAL');
          break;
        // 永続的なエラー
        case 'PowerBINotAuthorizedException':
        case 'LoadReportFailed':
          logToCloudWatch(`永続的エラー\nErrCode:${errorCode}\nErrlog:${errorMessage}\n詳細: ${detailedMessage}`,'FRONTEND_ERROR','CRITICAL');
          break;
        default:
          logToCloudWatch(`不明エラー\nErrCode:${errorCode}\nErrlog:${errorMessage}\n詳細: ${detailedMessage}`,'FRONTEND_ERROR','CRITICAL');
          break;
      }
      //console.error(`PowerBICtl Errlog:${errorMessage}\n詳細: ${detailedMessage}`);
    }]
  ]);

  return (
    <div className="responsive-container">
      <PowerBIEmbed
        embedConfig={embedConfig}
        eventHandlers={eventHandlers}
        cssClassName="report-style-class"
        getEmbeddedComponent={(embeddedReport) => {
          window.report = embeddedReport;
        }}
      />
    </div>
  );
};

export default PowerBICtl;
